'use client'

/**
 * Loading Location Form Drawer
 * Uses FormDrawer (UIKit) — proper header / scrollable body / sticky footer pattern.
 */

import { useEffect, useMemo } from 'react'
import { useTranslation, useForm, zodResolver, z, useSelector } from '@ufc/core'
import { selectUserTenant } from '@ufc/domains/auth'
import {
  FormDrawer,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Badge,
  Checkbox,
  cn,
} from '@ufc/uikit'
import { MapPin, Package, Building2, Truck } from 'lucide-react'
import type { LoadingLocation, LoadingLocationFormData, LocationKind } from '../../domain/types'
import { LOCATION_KINDS, LOCATION_KIND_CONFIG } from '../../domain/constants'

// ─── Schema ──────────────────────────────────────────────────────────────────

const formSchema = z.object({
  label:             z.string().min(1, 'Location name is required'),
  locationKind:      z.enum(['bay', 'silo'] as const),
  locationNumber:    z.preprocess(Number, z.number().min(1, 'Must be ≥ 1')),
  allowedVariantIds: z.array(z.string()).min(1, 'At least one variant is required'),
  variantId:         z.string().optional(),
  factoryId:         z.string().min(1, 'Factory is required'),
  maxVehicles:       z.preprocess(Number, z.number().min(1, 'Must be ≥ 1')),
  isActive:          z.boolean().default(true),
})

type FormValues = {
  label:             string
  locationKind:      'bay' | 'silo'
  locationNumber:    number
  allowedVariantIds: string[]
  variantId?:        string
  factoryId:         string
  maxVehicles:       number
  isActive:          boolean
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Variant {
  documentId: string
  name: { en: string; ar?: string } | string
  code?: string
  packaging?: string
}

export interface Factory {
  documentId: string
  name: string
}

export interface LoadingLocationFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  location?: LoadingLocation | null
  variants: Variant[]
  factories: Factory[]
  defaultFactoryId?: string
  onSubmit: (data: LoadingLocationFormData) => void
  isLoading?: boolean
  error?: string | null
  onClearError?: () => void
  isRTL?: boolean
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getVariantLabel(variant: Variant, lang: string): string {
  if (typeof variant.name === 'string') return variant.name
  return (lang === 'ar' && variant.name.ar) ? variant.name.ar : variant.name.en
}

// ─── Field Row ────────────────────────────────────────────────────────────────

function FieldRow({
  label,
  icon: Icon,
  error,
  children,
  hint,
}: {
  label: string
  icon: React.ElementType
  error?: string
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        {label}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

// ─── Section ─────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      {children}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LoadingLocationFormDrawer({
  open,
  onOpenChange,
  location,
  variants,
  factories,
  defaultFactoryId,
  onSubmit,
  isLoading = false,
  error,
  onClearError,
  isRTL = false,
}: LoadingLocationFormDrawerProps) {
  const { t, i18n } = useTranslation()
  const isEditing  = !!location

  // Backward compat: existing rows have `variant` set but `allowedVariants` empty.
  // Pre-seed allowedVariantIds from whichever field is populated.
  const defaultAllowedVariantIds = useMemo(() => {
    if (location?.allowedVariants?.length) {
      return location.allowedVariants.map((v) => v.documentId)
    }
    return location?.variant?.documentId ? [location.variant.documentId] : []
  }, [location])

  const defaultValues: FormValues = useMemo(
    () => ({
      label:             location?.label          ?? '',
      locationKind:      location?.locationKind   ?? 'bay',
      locationNumber:    location?.locationNumber ?? 1,
      allowedVariantIds: defaultAllowedVariantIds,
      variantId:         location?.variant?.documentId ?? undefined,
      factoryId:         location?.factory?.documentId ?? defaultFactoryId ?? '',
      maxVehicles:       location?.maxVehicles    ?? 1,
      isActive:          location?.isActive       ?? true,
    }),
    [location, defaultFactoryId, defaultAllowedVariantIds]
  )

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver:      zodResolver(formSchema) as any,
    defaultValues,
  })

  useEffect(() => {
    if (open) reset(defaultValues)
  }, [open, defaultValues, reset])

  useEffect(() => {
    if (!open || !location) return

    setValue('allowedVariantIds', defaultAllowedVariantIds, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    })
    setValue('variantId', location.variant?.documentId ?? undefined, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    })
    setValue('factoryId', location.factory?.documentId ?? defaultFactoryId ?? '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    })
  }, [open, location, defaultFactoryId, defaultAllowedVariantIds, setValue, variants.length, factories.length])

  const watchedKind            = watch('locationKind')
  const watchedAllowedVariants = watch('allowedVariantIds') ?? []
  const watchedVariant         = watch('variantId')
  const watchedFactory         = watch('factoryId')
  const watchedIsActive        = watch('isActive')
  const kindConfig             = LOCATION_KIND_CONFIG[watchedKind]

  // Filter variants by location kind: bay → bags packaging, silo → bulk packaging
  const filteredVariants = useMemo(() => {
    if (!watchedKind) return variants
    const targetPackaging = watchedKind === 'bay' ? 'bags' : 'bulk'
    return variants.filter((v) => v.packaging === targetPackaging)
  }, [variants, watchedKind])

  // When kind changes, drop any selected variant IDs that are no longer valid
  useEffect(() => {
    if (variants.length === 0) return
    const validIds = new Set(filteredVariants.map((v) => v.documentId))
    const kept = watchedAllowedVariants.filter((id) => validIds.has(id))
    if (kept.length !== watchedAllowedVariants.length) {
      setValue('allowedVariantIds', kept)
    }
    if (watchedVariant && !validIds.has(watchedVariant)) {
      setValue('variantId', undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredVariants, variants.length])

  // Variants that are currently checked (used for primary variant picker)
  const checkedVariants = useMemo(
    () => filteredVariants.filter((v) => watchedAllowedVariants.includes(v.documentId)),
    [filteredVariants, watchedAllowedVariants]
  )

  function toggleAllowedVariant(documentId: string) {
    const isChecked = watchedAllowedVariants.includes(documentId)
    const next = isChecked
      ? watchedAllowedVariants.filter((id) => id !== documentId)
      : [...watchedAllowedVariants, documentId]
    setValue('allowedVariantIds', next, { shouldValidate: true })
    // If the primary variant was unchecked, clear the primary selection too
    if (isChecked && watchedVariant === documentId) {
      setValue('variantId', undefined)
    }
  }

  const currentTenant = useSelector(selectUserTenant)

  const onFormSubmit = (data: FormValues) => {
    // Ensure the primary variant is always included in allowedVariantIds
    // (it's hidden from the checkbox list when selected, so force-include it)
    const allowedVariantIds = data.variantId && !data.allowedVariantIds.includes(data.variantId)
      ? [data.variantId, ...data.allowedVariantIds]
      : data.allowedVariantIds

    onSubmit({
      label:             data.label,
      locationKind:      data.locationKind,
      locationNumber:    data.locationNumber,
      allowedVariantIds,
      variantId:         data.variantId,
      factoryId:         data.factoryId,
      maxVehicles:       data.maxVehicles,
      isActive:          data.isActive,
      tenantId:          currentTenant?.id ? String(currentTenant.id) : undefined,
    })
  }

  // ── Header ──────────────────────────────────────────────────────────────────
  const headerContent = (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white leading-tight">
            {isEditing ? t('queue.editLocation') : t('queue.addLocation')}
          </h2>
          <p className="text-sm text-white/70">
            {isEditing ? location?.label : t('queue.locationForm.subtitle')}
          </p>
        </div>
      </div>
      {isEditing && (
        <div className="flex items-center gap-2 mt-3">
          <Badge
            className={cn('text-white border-white/30 bg-white/20 text-xs', kindConfig?.textClass)}
          >
            {i18n.language === 'ar' ? kindConfig?.labelAr : kindConfig?.label}
          </Badge>
          <Badge className={cn(
            'text-xs border-white/30',
            location?.isActive ? 'bg-emerald-500/30 text-emerald-100' : 'bg-red-500/30 text-red-100'
          )}>
            {location?.isActive ? t('common.active') : t('common.inactive')}
          </Badge>
        </div>
      )}
    </div>
  )

  return (
    <FormDrawer
      key={location?.documentId ?? 'new-loading-location'}
      open={open}
      onOpenChange={onOpenChange}
      headerContent={headerContent}
      headerGradient="from-violet-600 to-purple-700"
      title={isEditing ? t('queue.editLocation') : t('queue.addLocation')}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      error={error}
      onClearError={onClearError}
      onSave={handleSubmit(onFormSubmit as any)}
      saveText={isEditing ? t('common.save') : t('queue.addLocation')}
      cancelText={t('common.cancel')}
      isRTL={isRTL}
      fixedWidth={480}
      modal={false}
    >
      <div className="space-y-6">

        {/* ── Identity ─────────────────────────────────────────────── */}
        <Section title={t('queue.locationForm.sectionIdentity')}>

          <FieldRow
            label={t('queue.locationForm.label')}
            icon={MapPin}
            error={errors.label?.message}
          >
            <Input
              {...register('label')}
              placeholder={t('queue.locationForm.labelPlaceholder')}
              className="h-10"
            />
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow
              label={t('queue.locationForm.locationKind')}
              icon={MapPin}
              error={errors.locationKind?.message}
            >
              <Select
                value={watchedKind}
                onValueChange={(v: string) => setValue('locationKind', v as LocationKind)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder={t('queue.locationForm.selectLocationKind')} />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_KINDS.map((kind: LocationKind) => {
                    const cfg = LOCATION_KIND_CONFIG[kind]
                    return (
                      <SelectItem key={kind} value={kind}>
                        <span className="flex items-center gap-2">
                          <span className={cn('w-2 h-2 rounded-full', cfg.bgClass)} />
                          {i18n.language === 'ar' ? cfg.labelAr : cfg.label}
                        </span>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow
              label={t('queue.locationForm.locationNumber')}
              icon={MapPin}
              error={errors.locationNumber?.message}
            >
              <Input
                {...register('locationNumber')}
                type="number"
                min={1}
                className="h-10"
                placeholder="1"
              />
            </FieldRow>
          </div>

        </Section>

        {/* ── Assignment ───────────────────────────────────────────── */}
        <Section title={t('queue.locationForm.sectionAssignment')}>

          {/* Multi-select: which variants this location handles */}
          <FieldRow
            label={t('queue.locationForm.allowedVariants')}
            icon={Package}
            hint={t('queue.locationForm.allowedVariantsHelp')}
            error={(errors as any).allowedVariantIds?.message}
          >
            <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
              {filteredVariants.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">
                  {t('queue.locationForm.noVariants')}
                </p>
              ) : (
                filteredVariants
                  .filter((v) => v.documentId !== watchedVariant)
                  .map((v) => {
                    const checked = watchedAllowedVariants.includes(v.documentId)
                    return (
                      <label
                        key={v.documentId}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleAllowedVariant(v.documentId)}
                          id={`variant-${v.documentId}`}
                        />
                        <span className="flex items-center gap-2 text-sm select-none">
                          {getVariantLabel(v, i18n.language)}
                          {v.code && (
                            <span className="text-xs text-muted-foreground">({v.code})</span>
                          )}
                        </span>
                      </label>
                    )
                  })
              )}
            </div>
          </FieldRow>

          {/* Primary / default variant */}
          <FieldRow
              label={t('queue.locationForm.primaryVariant')}
              icon={Package}
              error={errors.variantId?.message}
            >
              <Select
                value={watchedVariant ?? '_none'}
                onValueChange={(v: string) =>
                  setValue('variantId', v === '_none' ? undefined : v)
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder={t('queue.locationForm.selectVariant')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">
                    <span className="text-muted-foreground">
                      — {t('queue.locationForm.noPrimary')} —
                    </span>
                  </SelectItem>
                  {checkedVariants.map((v: Variant) => (
                    <SelectItem key={v.documentId} value={v.documentId}>
                      <span className="flex items-center gap-2">
                        {getVariantLabel(v, i18n.language)}
                        {v.code && (
                          <span className="text-xs text-muted-foreground">({v.code})</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

          <FieldRow
            label={t('queue.locationForm.factory')}
            icon={Building2}
            error={errors.factoryId?.message}
          >
            <Select value={watchedFactory} onValueChange={(v: string) => setValue('factoryId', v)}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder={t('queue.locationForm.selectFactory')} />
              </SelectTrigger>
              <SelectContent>
                {factories.length === 0 ? (
                  <SelectItem value="_none" disabled>
                    {t('queue.locationForm.noFactories')}
                  </SelectItem>
                ) : (
                  factories.map((f) => (
                    <SelectItem key={f.documentId} value={f.documentId}>
                      {f.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </FieldRow>

        </Section>

        {/* ── Capacity ─────────────────────────────────────────────── */}
        <Section title={t('queue.locationForm.sectionCapacity')}>

          <FieldRow
            label={t('queue.locationForm.maxVehicles')}
            icon={Truck}
            error={errors.maxVehicles?.message}
            hint={t('queue.locationForm.maxVehiclesHelp')}
          >
            <Input
              {...register('maxVehicles')}
              type="number"
              min={1}
              className="h-10"
              placeholder="2"
            />
          </FieldRow>

        </Section>

        {/* ── Status ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{t('queue.locationForm.isActive')}</p>
            <p className="text-xs text-muted-foreground">{t('queue.locationForm.isActiveHelp')}</p>
          </div>
          <Switch
            checked={watchedIsActive}
            onCheckedChange={(v: boolean) => setValue('isActive', v)}
          />
        </div>

      </div>
    </FormDrawer>
  )
}
